import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, finalize, first, from, Observable, of, switchMap, tap } from "rxjs";
import { map } from "rxjs/operators";
import { AuthService as BaseAuthService } from "@kernel/tools/services/auth.service";
import { LocalStorageService } from "@kernel/tools/services/local-storage.service";
import AuthUserModelInterface from "@kernel/shared/interfaces/auth-user-model.interface";
import { GraphService } from "@kernel/graphql/services/graph.service";

const TOKEN_KEY = "auth_token";
const USER_KEY = "auth_user";

@Injectable({
  providedIn: "root",
})
export class AuthService extends BaseAuthService {
  private readonly router = inject(Router);
  private readonly localStorage = inject(LocalStorageService);
  private readonly graphService = inject(GraphService);

  private buildUserModel(raw: AuthUserModelInterface | null): AuthUserModelInterface | undefined {
    if (!raw) return undefined;
    const userPermissions = new Set(raw.permissions ?? []);
    return {
      ...raw,
      can: (permissions: string | (string | string[])[]) => {
        if (userPermissions.has("ALL_PERMISSIONS")) return true;
        const list = Array.isArray(permissions) ? permissions : [permissions];
        // outer array = OR logic; inner array = AND logic
        return list.some(item =>
          Array.isArray(item)
            ? item.every(p => userPermissions.has(p))
            : userPermissions.has(item)
        );
      },
    };
  }

  override getToken(): string | undefined {
    return this.localStorage.get<string>(TOKEN_KEY) ?? undefined;
  }

  override getUser(): AuthUserModelInterface | undefined {
    const raw = this.localStorage.get<AuthUserModelInterface>(USER_KEY);
    return this.buildUserModel(raw) ?? undefined;
  }

  setToken(token: string | null): void {
    if (token) {
      this.localStorage.set(TOKEN_KEY, token);
    } else {
      this.localStorage.remove(TOKEN_KEY);
    }
    this.token$.next(token ?? undefined);
  }

  setUser(user: Omit<AuthUserModelInterface, "can"> | null): void {
    if (user) {
      this.localStorage.set(USER_KEY, user);
    } else {
      this.localStorage.remove(USER_KEY);
    }
    this.user$.next(this.buildUserModel(user as AuthUserModelInterface));
  }

  isLoginCaptchaRequired(email: string): Observable<boolean> {
    return this.graphService
      .constructQuery<{ isLoginCaptchaRequired: boolean }>("", "isLoginCaptchaRequired", { email })
      .pipe(
        first(),
        map(data => data.data?.isLoginCaptchaRequired ?? false)
      );
  }

  private isLoggingOut = false;

  override logout(): Observable<void> {
    if (this.isLoggingOut) return of(undefined as void);
    this.isLoggingOut = true;
    return this.graphService.constructMutation('logout', {}, {}, []).pipe(
      first(),
      catchError(() => of(null)),
      tap(() => {
        this.localStorage.remove(TOKEN_KEY);
        this.localStorage.remove(USER_KEY);
        this.token$.next(undefined);
        this.user$.next(undefined);
      }),
      switchMap(() => from(this.router.navigate(["/auth/login"]))),
      map(() => undefined as void),
      finalize(() => { this.isLoggingOut = false; }),
    );
  }
}
